import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export async function uploadPreview(kind, file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(`/upload/preview?kind=${kind}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function uploadCommit(token, mapping, replaceOrMode = true) {
  const body = { token, mapping };
  if (typeof replaceOrMode === 'string') body.mode = replaceOrMode;
  else body.replace = !!replaceOrMode;
  const { data } = await api.post('/upload/commit', body);
  return data;
}

export async function getUploadStatus() {
  const { data } = await api.get('/upload/status');
  return data;
}

export async function resetData(kind) {
  const { data } = await api.post('/upload/reset', { kind });
  return data;
}

export async function getDashboardData(filters = {}) {
  const { data } = await api.post('/dashboard/data', filters);
  return data;
}

export async function getSchoolDetails(schoolId) {
  const { data } = await api.get(`/schools/${encodeURIComponent(schoolId)}`);
  return data;
}

export const FIELD_LABELS = {
  ministerial_id: 'الرقم الوزاري (مطلوب)',
  name: 'الاسم',
  region: 'المنطقة',
  governorate: 'المحافظة',
  education_office: 'إدارة التعليم',
  city: 'المدينة',
  school_type: 'نوع المدرسة',
  gender: 'الجنس',
  latitude: 'خط العرض (Latitude)',
  longitude: 'خط الطول (Longitude)',
  ticket_id: 'رقم البلاغ (مطلوب)',
  school_id: 'الرقم الوزاري للمدرسة',
  category: 'التصنيف / الفئة',
  status: 'الحالة',
  priority: 'الأولوية',
  contractor: 'المقاول',
  created_date: 'تاريخ الإنشاء',
  closed_date: 'تاريخ الإغلاق',
  // Supply Orders
  school_name: 'اسم المدرسة',
  company: 'الشركة الموردة',
  item: 'الصنف / البند',
  quantity: 'الكمية',
  delivery_date: 'تاريخ التوريد',
  notes: 'ملاحظات',
  // Suppliers
  supplier_id: 'رقم المورد',
  contact: 'جهة الاتصال',
  phone: 'رقم الجوال',
  email: 'البريد الإلكتروني',
  address: 'العنوان',
  // Items
  item_code: 'رقم/كود الصنف',
  unit: 'الوحدة',
  unit_price: 'سعر الوحدة',
  supplier: 'المورد',
  // LPC
  lpc_id: 'رقم LPC',
  amount: 'المبلغ',
  date: 'التاريخ',
  // Distribution
  distribution_id: 'رقم التوزيع',
  distribution_date: 'تاريخ التوزيع',
  // Evaluation
  evaluation_id: 'رقم التقييم',
  score: 'الدرجة / التقييم',
  quality: 'الجودة',
  timeliness: 'الالتزام بالمواعيد',
  // Commitments
  commitment_id: 'رقم الالتزام',
  due_date: 'تاريخ الاستحقاق',
  // Maintenance — Incident Categories
  category_id: 'رقم/كود التصنيف',
  parent: 'الفئة الرئيسية',
  severity: 'الخطورة / الأولوية',
  description: 'الوصف',
  // Maintenance — Work Orders
  work_order_id: 'رقم أمر العمل',
  engineer: 'المهندس',
  // Maintenance — Engineers / Supervisors
  employee_id: 'الرقم الوظيفي',
  specialty: 'التخصص',
  schools_count: 'عدد المدارس',
  // Maintenance — Field Visits
  visit_id: 'رقم الزيارة',
  supervisor: 'المشرف',
  visit_date: 'تاريخ الزيارة',
  purpose: 'الغرض / نوع الزيارة',
  findings: 'الملاحظات الفنية / التوصيات',
  duration: 'المدة',
};

export const REQUIRED_FIELDS = {
  schools: ['ministerial_id'],
  tickets: ['ticket_id', 'school_id'],
  // Supply: at least one of school_id/school_name required — enforced in the UI + backend.
  supply:  [],
  supply_suppliers:    ['name'],
  supply_items:        ['name'],
  supply_lpc:          [],
  supply_distribution: [],
  supply_evaluation:   [],
  supply_commitments:  [],
  // Maintenance modules
  maint_incident_cats: ['name'],
  maint_work_orders:   [],
  maint_engineers:     ['name'],
  maint_supervisors:   ['name'],
  maint_field_visits:  [],
};

// Supply module metadata for dedicated per-module upload pages
export const SUPPLY_MODULES = {
  supply_orders:       { kind: 'supply',              label: 'أوامر التوريد', color: '#22D3EE' },
  supply_suppliers:    { kind: 'supply_suppliers',    label: 'الموردون',       color: '#3B82F6' },
  supply_items:        { kind: 'supply_items',        label: 'الأصناف',        color: '#0EA5E9' },
  supply_lpc:          { kind: 'supply_lpc',          label: 'بيانات LPC',     color: '#8B5CF6' },
  supply_distribution: { kind: 'supply_distribution', label: 'التوزيع',        color: '#10B981' },
  supply_evaluation:   { kind: 'supply_evaluation',   label: 'التقييم',        color: '#F59E0B' },
  supply_commitments:  { kind: 'supply_commitments',  label: 'الالتزامات',     color: '#EF4444' },
};

export async function listSupplyModule(kind, { page = 1, page_size = 25, search = '' } = {}) {
  const { data } = await api.get(`/supply-modules/${kind}/list`, { params: { page, page_size, search } });
  return data;
}

// ── Dynamic Supply Importer ─────────────────────────────────
export async function dynamicSupplyPreview(file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/supply/dynamic-preview', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return data;
}

export async function dynamicSupplyRepick({ token, sheet_types }) {
  const { data } = await api.post('/supply/dynamic-repick', { token, sheet_types }, { timeout: 120000 });
  return data;
}

export async function dynamicSupplyReview({ token, supplier }) {
  const { data } = await api.post('/supply/dynamic-review', { token, supplier }, { timeout: 120000 });
  return data;
}
export async function dynamicSupplyEdit({ token, review_key, field, value }) {
  const { data } = await api.post('/supply/dynamic-edit', { token, review_key, field, value });
  return data;
}
export async function dynamicSupplyEditRevert({ token, review_key, field }) {
  const { data } = await api.post('/supply/dynamic-edit-revert', { token, review_key, field });
  return data;
}
export async function dynamicSupplyEditRevertAll({ token }) {
  const { data } = await api.post('/supply/dynamic-edit-revert-all', { token });
  return data;
}

export async function dynamicSupplyCommit({ token, supplier, mode = 'merge', force = false }) {
  const { data } = await api.post('/supply/dynamic-commit', { token, supplier, mode, force }, { timeout: 180000, validateStatus: (s) => (s >= 200 && s < 300) || s === 409 });
  return data;
}

export async function dynamicSupplyDiff({ token, supplier, mode = 'merge', mapping }) {
  const { data } = await api.post('/supply/dynamic-diff', { token, supplier, mode, mapping }, { timeout: 120000 });
  return data;
}

export async function dynamicSupplyRaw({ token, sheet, limit = 20 }) {
  const { data } = await api.post('/supply/dynamic-raw', { token, sheet, limit }, { timeout: 60000 });
  return data;
}

export async function dynamicSupplyCommitManual({ token, supplier, mapping, mode = 'merge' }) {
  const { data } = await api.post('/supply/dynamic-commit-manual', { token, supplier, mapping, mode }, { timeout: 180000 });
  return data;
}

// ── Supply Executive Dashboard ─────────────────────────────────
export async function getSupplyExecutiveDashboard() {
  const { data } = await api.get('/supply/executive-dashboard', { timeout: 60000 });
  return data;
}

export async function getSupplySchoolDetails(schoolId) {
  const { data } = await api.get(`/supply/school/${encodeURIComponent(schoolId)}`);
  return data;
}

export async function getSupplierDetails(name) {
  const { data } = await api.get(`/supply/supplier/${encodeURIComponent(name)}`, { timeout: 60000 });
  return data;
}

// ── Master Upgrade ────────────────────────────────────────────
export async function globalSearch(query, limit = 8) {
  if (!query || !query.trim()) return null;
  const { data } = await api.get(`/supply/search`, { params: { q: query, limit } });
  return data;
}

export async function getAdministrations() {
  const { data } = await api.get('/supply/administrations');
  return data;
}

export async function getAdministrationProfile(name) {
  const { data } = await api.get(`/supply/administration/${encodeURIComponent(name)}`, { timeout: 60000 });
  return data;
}

export async function getDataQuality() {
  const { data } = await api.get('/supply/data-quality', { timeout: 60000 });
  return data;
}

// ── v6.9 Supplier admin + manual entry + map ──
export async function deleteSupplier(name) {
  const { data } = await api.delete(`/supply/supplier/${encodeURIComponent(name)}`);
  return data;
}
export async function addManualSupplyRecord(payload) {
  const { data } = await api.post('/supply/manual', payload);
  return data;
}
export async function getMapByAdmin(params = {}) {
  const { data } = await api.get('/supply/map-by-admin', { params, timeout: 60000 });
  return data;
}
